import React, { useState } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  FormHelperText,
} from '@mui/material';
import ClockLoader from 'shared/components/ClockLoader';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import { SERVICE_CODE_OPTIONS } from 'shared/constants/serviceCodes';

const STATUS_OPTIONS = [
  'ACTIVE',
  'DISABLED',
  'PENDING',
  'DEPLOYED',
  'EXPIRED',
  'DEFAULT',
];

const defaultInitial = {
  serviceName: '',
  serviceCode: '',
  serviceDescription: '',
  numberOfUsers: '',
  startDate: '',
  endDate: '',
  status: 'ACTIVE',
};

const validationSchema = yup.object({
  serviceName: yup.string().trim().required('Service name is required'),
  serviceCode: yup
    .string()
    .trim()
    .oneOf(
      SERVICE_CODE_OPTIONS.map((option) => option.value),
      'Select a valid service type'
    )
    .required('Service code is required'),
  serviceDescription: yup.string().trim(),
  numberOfUsers: yup
    .string()
    .trim()
    .test('num', 'Enter a non-negative whole number', (val) => {
      if (val == null || val === '') return true;
      return /^\d+$/.test(val);
    }),
  startDate: yup.string(),
  endDate: yup.string(),
  status: yup.string().required('Status is required'),
});

const toPayload = (values) => ({
  serviceName: values.serviceName.trim(),
  serviceCode: values.serviceCode.trim(),
  serviceDescription: values.serviceDescription?.trim() || undefined,
  numberOfUsers:
    values.numberOfUsers === '' || values.numberOfUsers == null
      ? undefined
      : Number(values.numberOfUsers),
  startDate: values.startDate?.trim() || undefined,
  endDate: values.endDate?.trim() || undefined,
  status: values.status,
});

const AddServiceDialog = ({ open, onClose, onSuccess, createService }) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      await createService(toPayload(values));
      toast.success('Service created successfully');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to create service';
      toast.error(typeof err === 'string' ? err : 'Failed to create service');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Add service</DialogTitle>
      <Formik
        initialValues={defaultInitial}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <DialogContent>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                  pt: 1,
                }}
              >
                <TextField
                  fullWidth
                  name="serviceName"
                  label="Service name"
                  value={values.serviceName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.serviceName && Boolean(errors.serviceName)}
                  helperText={touched.serviceName && errors.serviceName}
                />
                <FormControl
                  fullWidth
                  error={touched.serviceCode && Boolean(errors.serviceCode)}
                >
                  <InputLabel id="add-service-code">Service type</InputLabel>
                  <Select
                    labelId="add-service-code"
                    name="serviceCode"
                    label="Service type"
                    value={values.serviceCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {SERVICE_CODE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {(touched.serviceCode && errors.serviceCode) ||
                      'PAYROLL 111, CONTRACT 222, IT GOVERNANCE 333'}
                  </FormHelperText>
                </FormControl>
                <TextField
                  fullWidth
                  name="numberOfUsers"
                  label="Number of users (optional)"
                  type="number"
                  value={values.numberOfUsers}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.numberOfUsers && Boolean(errors.numberOfUsers)}
                  helperText={touched.numberOfUsers && errors.numberOfUsers}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  fullWidth
                  name="startDate"
                  label="Start date"
                  type="date"
                  value={values.startDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  name="endDate"
                  label="End date"
                  type="date"
                  value={values.endDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  InputLabelProps={{ shrink: true }}
                />
                <FormControl fullWidth>
                  <InputLabel id="add-service-status">Status</InputLabel>
                  <Select
                    labelId="add-service-status"
                    name="status"
                    label="Status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  name="serviceDescription"
                  label="Description"
                  value={values.serviceDescription}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiline
                  minRows={2}
                  sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}
                />
                
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting}>
                {submitting ? <ClockLoader inline size={22} /> : 'Save'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddServiceDialog;
